<template>
  <div
    class="flex h-64 w-full items-center justify-center sm:h-72 md:h-96 lg:absolute lg:inset-y-0 lg:right-0 lg:h-full"
    :class="[
      {
        'lg:w-1/2': position !== 'full-width' && blok.style !== 'cards',
        'lg:w-2/3': blok.style === 'cards',
        'left-0': position === 'left',
      },
    ]"
  >
    <div
      :id="`heroImgContainer_${blok._uid}`"
      class="hero-img-container relative h-full w-full overflow-hidden"
      :class="[
        { 'pos-top': blok.mobileImageFirst },
        `pos-${position}`,
        position !== 'full-width' ? `separator-${separatorStyle}` : '',
      ]"
    >
      <video
        v-if="video?.filename || videoMobile?.filename"
        ref="videoElement"
        loop="true"
        muted="true"
        disablepictureinpicture="true"
        disableremoteplayback="true"
        playsinline="true"
        preload="none"
        class="pointer-events-none absolute inset-0 h-full w-full object-cover"
        :class="{
          'px-4 sm:px-8 lg:px-0': position !== 'full-width' && imageWidth < 100,
        }"
        :poster="imgUrl || null"
        aria-label="Achtergrond animatie"
      >
        <source
          v-if="videoUrl"
          :src="videoUrl"
          :type="`video/${getFileExtension(videoUrl)}`"
        />
        Je browser ondersteunt geen video's.
        <a v-if="videoUrl" :href="videoUrl">Download hier de video.</a>
      </video>

      <img
        v-else
        :src="img"
        class="absolute inset-0 h-full w-full object-cover"
        :class="{
          'px-4 sm:px-8 lg:px-0': position !== 'full-width' && imageWidth < 100,
          'lg:hidden': video?.filename,
          'hidden lg:block': videoMobile?.filename,
        }"
        :alt="imgAlt"
        :srcset="imgSrcSet"
        :sizes="imgSizes"
      />

      <div
        v-if="blok.overlayColor"
        class="absolute inset-0 mix-blend-multiply"
        :class="backgroundColorClass(blok.overlayColor)"
      />

      <ImgAdapter
        v-if="
          (blok.logoOverImage && blok.logoOverImage?.filename) ||
          blok.logoOverImageUrl
        "
        :img-sources="[blok.logoOverImage, blok.logoOverImageUrl]"
        alt=""
        class="absolute m-2"
        :class="logoPosition"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeMount, onMounted } from 'vue'
import ImgAdapter from '../components/ImgAdapter.vue'
import { usePageContext } from '../renderer/usePageContext.js'
import { useColorPalette } from '../composables/colorPalette'
import { getImgSrc, getImgSrcset } from '../utils/imgUtils'
import { useHead } from '@unhead/vue'

const props = defineProps({
  imgUrl: {
    type: String,
    required: true,
  },
  imgAlt: {
    type: String,
    required: false,
    default: '',
  },
  video: {
    type: Object,
    default: () => {},
  },
  videoMobile: {
    type: Object,
    default: () => {},
  },
  blok: {
    type: Object,
    default: () => {},
  },
})

const { backgroundColorClass } = useColorPalette()

const videoUrl = ref('')
const videoElement = ref(null)

const img = computed(() => {
  return getImgSrc({
    imgUrl: props.imgUrl,
    smart: props.blok.backgroundImageSmartCrop,
  })
})
const imgSrcSet = computed(() => {
  return getImgSrcset({
    imgUrl: props.imgUrl,
    smart: props.blok.backgroundImageSmartCrop,
    dimensions: [
      { width: 640 },
      { width: 1024 },
      { width: 1920 },
      { width: 2048 },
    ],
  })
})
const imgSizes = computed(() => {
  return `(max-width: 1023px) 100vw, ${
    position.value === 'full-width' ? '100vw' : '50vw'
  }`
})
const imageWidth = computed(() => {
  return props.blok.backgroundImageWidth || 100
})
const imageHeight = computed(() => {
  return props.blok.backgroundImageHeight || 100
})
const position = computed(() => {
  return props.blok.backgroundPosition || 'right'
})
const separatorStyle = computed(() => {
  return props.blok.separatorStyle || 'vertical'
})
const logoPosition = computed(() => {
  if (props.blok.logoOverImagePosition) {
    const map = {
      'top-right': 'top-0 right-0',
      'top-left': 'top-0 left-0',
      'bottom-right': 'bottom-0 right-0',
      'bottom-left': 'bottom-0 left-0',
    }
    return map[props.blok.logoOverImagePosition]
  }
  return 'top-0 right-0'
})

function getFileExtension(filename) {
  const filenameParts = filename?.split('.') || []
  return (filenameParts.length && filenameParts[filenameParts.length - 1]) || ''
}

onBeforeMount(() => {
  // We need to check if we're on desktop or mobile to load only the correct video file and avoid loading extra videos that
  // are not visible. Using display:none on the video element is not enough

  // Desktop resolution
  if (props.video?.filename && window.innerWidth >= 992) {
    videoUrl.value = props.video.filename
  } else if (props.videoMobile?.filename) {
    videoUrl.value = props.videoMobile.filename
  }
})

onMounted(() => {
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  // Don't autoplay video for users that have prefers-reduced-motion enabled
  if (videoUrl.value && !motionQuery.matches) {
    videoElement.value.setAttribute('autoplay', '')
  }
})

const pageContext = usePageContext()
useHead(
  () => {
    const style = []
    if (position.value !== 'full-width') {
      style.push({
        nonce: pageContext.nonce,
        innerHTML: `
          #heroImgContainer_${props.blok._uid} {
            height: ${imageHeight.value}% !important;
          }
          @media (min-width: 1024px) {
            #heroImgContainer_${props.blok._uid} {
              width: ${imageWidth.value}% !important;
            }
          }`,
      })
    }

    return {
      style,
      link: [
        {
          rel: 'preload',
          as: 'image',
          href: img.value,
          imagesrcset: imgSrcSet.value,
          imagesizes: imgSizes.value,
        },
      ],
    }
  },
  { mode: import.meta.env.VITE_IS_EDITOR ? 'client' : 'server' },
)
</script>

<style scoped>
.hero-img-container.separator-slanted {
  clip-path: polygon(0 10%, 100% 0, 100% 100%, 0 100%);
}

.hero-img-container.separator-slanted.pos-top {
  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 90%);
}

.hero-img-container.separator-round-corner {
  border-top-left-radius: 40%;
}

.hero-img-container.separator-round-corner.pos-top {
  border-top-left-radius: 0;
  border-bottom-left-radius: 40%;
}

.hero-img-container.separator-cut-corner {
  clip-path: polygon(12% 0, 100% 0, 100% 76%, 88% 100%, 0 100%, 0 24%);
}

.hero-img-container.separator-cut-corner.pos-top {
  clip-path: polygon(12% 0, 100% 0, 100% 76%, 88% 100%, 0 100%, 0 24%);
}

@media (min-width: 1024px) {
  .hero-img-container.separator-slanted.pos-right {
    clip-path: polygon(15% 0, 100% 0%, 100% 100%, 0% 100%);
  }

  .hero-img-container.separator-slanted.pos-left {
    clip-path: polygon(0 0, 100% 0%, 85% 100%, 0% 100%);
  }

  .hero-img-container.separator-round-corner.pos-right {
    border-top-left-radius: 0;
    border-bottom-left-radius: 40%;
  }

  .hero-img-container.separator-round-corner.pos-left {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 40%;
  }

  .hero-img-container.separator-cut-corner.pos-right {
    clip-path: polygon(15% 0, 100% 0, 100% 85%, 85% 100%, 0 100%, 0 15%);
  }

  .hero-img-container.separator-cut-corner.pos-left {
    clip-path: polygon(15% 0, 100% 0, 100% 85%, 85% 100%, 0 100%, 0 15%);
  }
}
</style>
